import { RoutePath } from 'enums/Routes';
import {
  ConfirmLoanResult,
  ConfirmYourDetailsResult,
  EmploymentDetailsResult,
  FeedbackResult,
  StepsResult,
  SelectDebtsResult,
  AdditionalFundsResult,
  VerifyIncomeResult,
  RepaymentMethodResult,
  RepaymentDecisionResult,
  SetupAccountResult,
} from 'enums/FlowNextResults';
import SetupAccount from 'components/Verification/VerificationHome';
import { VerifyStatus } from 'api/LoanOfferApi';
import ConfirmLoan from 'components/ConfirmLoan';
import DocuSign from 'components/DocuSign';
import EmailNotificationAlert from 'components/EmailNotificationAlert';
import Feedback from 'components/Feedback';
import FundsSent from 'components/FundsSent';
import NextSteps from 'components/NextSteps';
import OfferStatus from 'components/OfferStatus';
import RepaymentMethod from 'components/RepaymentMethod';
import SetupDeduction from 'components/SetupDeduction';
import NotifyYourTeam from 'components/SetupDeduction/ManualDeduction/components/NotifyYourTeam';
import SetupViaPayroll from 'components/SetupDeduction/ManualDeduction/components/SetupViaPayroll';
import VerifyPayrollDeduction from 'components/SetupDeduction/ManualDeduction/components/VerifyPayrollDeduction';
import PaystubsSignInGuide from 'components/SetupDeduction/ManualDeduction/components/PaystubsSignInGuide';
import WhatIsTheIssue from 'components/SetupDeduction/ManualDeduction/components/WhatIsTheIssue';
import Alloy from 'components/Verification/UploadLicense';
import UploadIncomeVerification from 'components/Verification/Steps/IncomeVerification/Upload/UploadIncomeVerification';
import VerifyIncome from 'components/Verification/Steps/CheckingAccount/VerifyIncome/VerifyIncome';
import { VerificationNavigation } from 'components/Verification/verificationSteps';
import VerifyingApplication from 'components/VerifyingApplication/VerifyingApplication';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { updateApplicationStatus } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';
import OfferAvailable from 'components/DebtConsolidation/OfferAvailable';
import ConfirmYourDetails from 'components/LoanForm/ConfirmYourDetails';
import AdditionalFunds from 'components/AdditionalFunds';
import UnfreezeCreditScore from 'components/UnfreezeCreditScore';
import SelectDebts from 'components/SelectDebts';
import { YourGoalVariant } from 'components/LoanGoals/LoanGoals';
import OpenDDA from 'components/RepaymentMethod/OpenDDA';
import { HardOfferData } from 'handlers/applicationData';
import { PaymentMethod } from 'enums/PaymentMethod';

import { getInjectedStoreState } from 'store/injectStore';
import { FLOW_TO_STEP_MAP } from 'handlers/applicationStep';
import { CurrentFlow } from 'enums/CurrentFlow';

import { NextFunction, RouterType, CustomRouterType } from './types';
import { handleDebtConsolidationApply } from './FlowRouter';

export const EMPLOYEE_LENDING_TITLE = 'Your Offer';
export const VERIFICATION_TITLE = 'Verification';
export const SETUP_DEDUCTION_TITLE = 'Repayment';

const handleRepaymentMethodNavigation: CustomRouterType['handleNext'] = (args) => () => {
  const { navigate, dispatchWithUnwrap, state } = args;
  const { application } = getApplicationData(state);
  const isACHPaymentMethod = application?.paymentMethod === PaymentMethod.ACH;

  dispatchWithUnwrap(
    updateApplicationStatus({
      applicationId: application!.id,
      applicationStatus: isACHPaymentMethod ? ApplicationStatusName.FinalReview : ApplicationStatusName.SetUpDeduction,
    }),
  );

  if (isACHPaymentMethod) {
    navigate(RoutePath.OfferStatus);
  } else {
    navigate(RoutePath.RepaymentMethod);
  }
};

export const getDebtConsolidationRoutes = (next: NextFunction): RouterType => ({
  [RoutePath.OfferAvailable]: {
    navigationInfo: { showBackLink: false, title: EMPLOYEE_LENDING_TITLE },
    component: OfferAvailable,
    handleNext: ({ navigate }) => () => {
      navigate(RoutePath.ConfirmLoan);
    },
  },
  [RoutePath.ConfirmLoan]: {
    navigationInfo: { showBackLink: true, title: 'Your Plan' },
    component: ConfirmLoan,
    handleNext: (args) => (result) => {
      const { navigate } = args;
      switch (result) {
        case ConfirmLoanResult.Continue:
          navigate(RoutePath.VerificationSteps);
          break;
        case ConfirmLoanResult.BackToFinancialDashboard:
          next(args)(undefined);
          break;
        default:
          navigate(RoutePath.Error);
          break;
      }
    },
  },
  [RoutePath.SetupAccount]: {
    component: SetupAccount,
    handleNext: ({ navigate }) => (result) => {
      switch (result) {
        case SetupAccountResult.AllStepsCompleted:
          navigate(RoutePath.VerifyingApplication);
          break;
        default:
          navigate(RoutePath.VerificationSteps);
          break;
      }
    },
  },
  [RoutePath.VerificationSteps]: {
    component: VerificationNavigation,
    handleNext: ({ navigate }) => async (result) => {
      switch (result) {
        case StepsResult.Completed:
          navigate(RoutePath.VerifyingApplication);
          break;
        case EmploymentDetailsResult.Continue:
          break;
        case EmploymentDetailsResult.Rejected:
        case StepsResult.Rejected:
          navigate(RoutePath.OfferStatus);
          break;
        default:
          navigate(RoutePath.Error);
          break;
      }
    },
  },
  [RoutePath.VerifyingApplication]: {
    navigationInfo: { showBackLink: false, title: VERIFICATION_TITLE },
    component: VerifyingApplication,
    handleNext: ({ navigate }) => async (result) => {
      // For some reason, the state argument gives an older snapshot that does not provide the application.
      const state = getInjectedStoreState();
      switch (result) {
        case VerifyStatus.PendingDocuments:
          navigate(RoutePath.Alloy);
          break;
        case VerifyStatus.PendingPaysubs:
          navigate(RoutePath.UploadIncomeVerification);
          break;
        case VerifyStatus.Verified:
          // Go to the next step the BE tells us to go to
          navigate(FLOW_TO_STEP_MAP[CurrentFlow.DebtConsolidation](state.applicationData.application).path);
          break;
        case VerifyStatus.ManualVerify:
        case VerifyStatus.Rejected:
          navigate(RoutePath.OfferStatus);
          break;
        default:
          navigate(RoutePath.Error);
      }
    },
  },
  [RoutePath.Alloy]: {
    navigationInfo: { showBackLink: false, title: VERIFICATION_TITLE },
    component: Alloy,
    handleNext: ({ navigate }) => async () => {
      navigate(RoutePath.VerifyingApplication);
    },
  },
  [RoutePath.UploadIncomeVerification]: {
    navigationInfo: { showBackLink: false, title: VERIFICATION_TITLE },
    component: UploadIncomeVerification,
    handleNext: ({ navigate }) => async () => {
      navigate(RoutePath.VerifyingApplication);
    },
  },
  [RoutePath.VerifyIncome]: {
    navigationInfo: { showBackLink: false, title: VERIFICATION_TITLE },
    component: VerifyIncome,
    handleNext: ({ navigate }) => async (result) => {
      switch (result) {
        case VerifyIncomeResult.UpdateAnnualIncome: {
          navigate(RoutePath.AnalyzingUpdatedOffer);
          break;
        }
        default:
          navigate(RoutePath.UploadIncomeVerification);
      }
    },
  },
  [RoutePath.RepaymentMethod]: {
    navigationInfo: { showBackLink: false, title: SETUP_DEDUCTION_TITLE },
    component: RepaymentMethod,
    handleNext: ({ navigate }) => (result) => {
      switch (result) {
        case RepaymentMethodResult.CompletedRepaymentAccountStep:
          navigate(RoutePath.SetupDeduction);
          break;
        default:
          navigate(RoutePath.OpenDDA);
      }
    },
  },
  [RoutePath.OpenDDA]: {
    navigationInfo: { title: SETUP_DEDUCTION_TITLE, step: 1, stepCount: 3 },
    component: OpenDDA,
    handleNext: ({ navigate }) => () => {
      navigate(RoutePath.SetupDeduction);
    },
  },
  [RoutePath.SetupDeduction]: {
    navigationInfo: { showBackLink: false, title: SETUP_DEDUCTION_TITLE, step: 2, stepCount: 3 },
    component: SetupDeduction,
    handleNext: ({ state, dispatchWithUnwrap, navigate }) => async (result) => {
      switch (result) {
        case RepaymentDecisionResult.No:
          navigate(RoutePath.NotifyYourTeam);
          break;
        case RepaymentDecisionResult.Yes:
          navigate(RoutePath.PaystubsSignInGuide);
          break;
        default: {
          const { application } = getApplicationData(state!);
          await dispatchWithUnwrap(
            updateApplicationStatus({
              applicationId: application!.id,
              applicationStatus: ApplicationStatusName.FinalReview,
            }),
          );
          navigate(RoutePath.OfferStatus);
        }
      }
    },
  },
  [RoutePath.PaystubsSignInGuide]: {
    navigationInfo: { showBackLink: true, title: SETUP_DEDUCTION_TITLE, step: 2, stepCount: 3 },
    component: PaystubsSignInGuide,
    handleNext: ({ navigate }) => (result) => {
      switch (result) {
        case RepaymentDecisionResult.No:
          navigate(RoutePath.NotifyYourTeam);
          break;
        default:
          navigate(RoutePath.SetupViaPayroll);
      }
    },
  },
  [RoutePath.SetupViaPayroll]: {
    navigationInfo: { title: SETUP_DEDUCTION_TITLE, step: 3, stepCount: 3 },
    component: SetupViaPayroll,
    handleNext: ({ navigate }) => (result) => {
      switch (result) {
        case RepaymentDecisionResult.No:
          navigate(RoutePath.WhatIsTheIssue);
          break;
        default:
          navigate(RoutePath.VerifyPayrollDeduction);
      }
    },
  },
  [RoutePath.WhatIsTheIssue]: {
    component: WhatIsTheIssue,
    navigationInfo: { title: SETUP_DEDUCTION_TITLE, step: 3, stepCount: 3 },
    handleNext: ({ navigate }) => (errorOptionSelected) => {
      navigate(RoutePath.NotifyYourTeam, { state: { errorOptionSelected } });
    },
  },
  [RoutePath.NotifyYourTeam]: {
    component: NotifyYourTeam,
    navigationInfo: { title: 'Notify your team' },
    handleNext: ({ dispatchWithUnwrap, state, navigate }) => async () => {
      const { application } = getApplicationData(state!);

      await dispatchWithUnwrap(
        updateApplicationStatus({
          applicationId: application!.id,
          applicationStatus: ApplicationStatusName.FinalReview,
        }),
      );

      navigate(RoutePath.VerifyPayrollDeduction);
    },
  },
  [RoutePath.VerifyPayrollDeduction]: {
    navigationInfo: { showBackLink: false, title: SETUP_DEDUCTION_TITLE },
    component: VerifyPayrollDeduction,
    handleNext: ({ navigate }) => () => {
      navigate(RoutePath.OfferStatus);
    },
  },
  [RoutePath.DocuSignSignature]: {
    component: DocuSign,
  },
  [RoutePath.EmailNotificationAlert]: {
    // Can we remove this?
    component: EmailNotificationAlert,
  },
  [RoutePath.NextSteps]: {
    component: NextSteps,
    handleNext: next,
  },
  [RoutePath.OfferStatus]: {
    component: OfferStatus,
    handleNext: next,
  },
  [RoutePath.UnfreezeCreditScore]: {
    component: UnfreezeCreditScore,
    handleNext: handleDebtConsolidationApply,
  },
  [RoutePath.Feedback]: {
    component: Feedback,
    navigationInfo: { showBackLink: true, title: 'Feedback' },
    handleNext: ({ navigate }) => (result) => {
      switch (result) {
        case FeedbackResult.NextSteps:
          navigate(RoutePath.NextSteps);
          break;
        case FeedbackResult.CrossSell:
          navigate(RoutePath.ApplyEmployeeLoan);
          break;
        default:
          navigate(RoutePath.FinancialDashboard);
          break;
      }
    },
  },
  [RoutePath.FundsSent]: {
    component: FundsSent,
  },
  [RoutePath.ConfirmYourDetails]: {
    navigationInfo: { showBackLink: true, title: 'Apply', step: 1, stepCount: 3 },
    component: ConfirmYourDetails,
    handleNext: ({ navigate }) => (result) => {
      switch (result) {
        case ConfirmYourDetailsResult.UpdateFields:
          navigate(RoutePath.YourName, {
            state: { isFromConfirmDetails: true },
          });
          break;
        default:
          navigate(RoutePath.Goals, {
            state: {
              variant: YourGoalVariant.CardCrossSell,
            },
          });
          break;
      }
    },
  },
  [RoutePath.AdditionalFunds]: {
    component: AdditionalFunds,
    handleNext: ({ navigate }) => () => {
      navigate(RoutePath.ConfirmLoan);
    },
  },
  [RoutePath.SelectDebts]: {
    component: SelectDebts,
    handleNext: (args) => (result) => {
      const { navigate } = args;

      switch (result) {
        case SelectDebtsResult.FinalizeReviewOffer:
          navigate(RoutePath.FinalizeReviewOffer);
          break;
        case SelectDebtsResult.FundsRemaining: {
          navigate(RoutePath.FinalizeAdditionalFunds);
          break;
        }
        case SelectDebtsResult.NoChanges:
        default:
          handleRepaymentMethodNavigation(args)(result);
          break;
      }
    },
  },
  [RoutePath.FinalizeAdditionalFunds]: {
    component: AdditionalFunds,
    handleNext: (args) => (result) => {
      const { navigate, state } = args;
      // Warning: This state value is stale but for this purpose it doesn't matter
      const { application } = getApplicationData(state);
      const data = application?.hardOffer as HardOfferData;

      switch (result) {
        case AdditionalFundsResult.ModifiedFunds:
          navigate(RoutePath.FinalizeReviewOffer);
          break;
        case AdditionalFundsResult.NoChanges:
        default:
          if (data.manuallyUpdatedSelections) {
            navigate(RoutePath.FinalizeReviewOffer);
          } else {
            handleRepaymentMethodNavigation(args)(result);
          }
          break;
      }
    },
  },
  [RoutePath.FinalizeReviewOffer]: {
    component: ConfirmLoan,
    handleNext: (args) => (result) => {
      const { navigate } = args;

      switch (result) {
        case ConfirmLoanResult.Continue:
          handleRepaymentMethodNavigation(args)(result);
          break;
        case ConfirmLoanResult.MakeChanges:
          navigate(RoutePath.SelectDebts);
          break;
        case ConfirmLoanResult.BackToFinancialDashboard:
          next(args)(undefined);
          break;
        default:
          navigate(RoutePath.Error);
      }
    },
  },
});
